import React, { useState, useEffect } from 'react';
import { Palette, User, Settings as SettingsIcon, Check, Circle, Menu } from 'lucide-react';
import axios from 'axios';
import SidebarNav from '../components/SidebarNav';
import Button from '../components/common/Button';
import './SettingsPage.css';

const SettingsPage = ({ 
    currentPage, 
    onNavigate, 
    onSignOut,
    userProfile, 
    setUserProfile,
    darkMode,
    setDarkMode,
    onShowAuth
}) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [localProfile, setLocalProfile] = useState(userProfile || {});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setLocalProfile(userProfile || {});
    }, [userProfile]);

    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    const handleProfileChange = (field, value) => {
        setLocalProfile(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await axios.post(`${apiBase}/api/profile/`, {
                ...localProfile,
                action: 'update'
            });
            setUserProfile(response.data);
            localStorage.setItem('spotlogs_profile', JSON.stringify(response.data));
            setHasChanges(false);
        } catch (error) {
            console.error("Error updating profile:", error);
            alert("Failed to save profile to cloud.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDiscard = () => {
        setLocalProfile(userProfile || {});
        setHasChanges(false);
    };

    return (
        <div className="settings-layout">
            <SidebarNav 
                currentPage={currentPage} 
                onNavigate={onNavigate}
                onSignOut={onSignOut}
                isMobileOpen={isMobileNavOpen}
                onCloseMobile={() => setIsMobileNavOpen(false)}
                userProfile={userProfile}
                onShowAuth={onShowAuth}
            />

            <main className="settings-main">
                <div className="settings-container">
                    <header className="settings-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button 
                                className="mobile-menu-btn"
                                onClick={() => setIsMobileNavOpen(true)}
                            >
                                <Menu size={24} />
                            </button>
                            <h2>App Settings</h2>
                        </div>
                        <p>Manage your profile, trucking details, and application preferences.</p>
                    </header>

                    <div className="settings-content">
                        {/* Appearance Section */}
                        <section className="settings-section">
                            <h3 className="section-title">
                                <Palette size={20} />
                                Appearance
                            </h3>
                            <div className="theme-cards">
                                <button 
                                    className={`theme-card ${!darkMode ? 'selected' : ''}`}
                                    onClick={() => setDarkMode(false)}
                                >
                                    <div className="theme-card-header">
                                        <span>Light Mode</span>
                                        {!darkMode ? <Check size={20} /> : <Circle size={20} />}
                                    </div>
                                    <div className="theme-preview light-preview">
                                        <div className="preview-line long"></div>
                                        <div className="preview-line full"></div>
                                        <div className="preview-line short"></div>
                                    </div>
                                </button>

                                <button 
                                    className={`theme-card ${darkMode ? 'selected' : ''}`}
                                    onClick={() => setDarkMode(true)}
                                >
                                    <div className="theme-card-header">
                                        <span>Dark Mode</span>
                                        {darkMode ? <Check size={20} /> : <Circle size={20} />}
                                    </div>
                                    <div className="theme-preview dark-preview">
                                        <div className="preview-line long"></div>
                                        <div className="preview-line full"></div>
                                        <div className="preview-line short"></div>
                                    </div>
                                </button>
                            </div>
                        </section>

                        {/* Profile Information */}
                        <section className="settings-section glass-card">
                            <div className="section-header">
                                <h3 className="section-title">
                                    <User size={20} />
                                    Profile Information
                                </h3>
                                <button className="edit-link">Edit Details</button>
                            </div>
                            <div className="profile-grid">
                                <div className="profile-field">
                                    <label>Driver Name</label>
                                    <input 
                                        type="text"
                                        value={localProfile.driverName || ''}
                                        onChange={(e) => handleProfileChange('driverName', e.target.value)}
                                        autoComplete="username"
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Carrier Name</label>
                                    <input 
                                        type="text"
                                        value={localProfile.carrierName || ''}
                                        onChange={(e) => handleProfileChange('carrierName', e.target.value)}
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Vehicle Unit #</label>
                                    <input 
                                        type="text"
                                        value={localProfile.vehicleUnit || ''}
                                        onChange={(e) => handleProfileChange('vehicleUnit', e.target.value)}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Truck Plate</label>
                                    <input 
                                        type="text"
                                        value={localProfile.truckPlate || ''}
                                        onChange={(e) => handleProfileChange('truckPlate', e.target.value)}
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Main Office Address</label>
                                    <input 
                                        type="text"
                                        value={localProfile.officeAddress || ''}
                                        onChange={(e) => handleProfileChange('officeAddress', e.target.value)}
                                        placeholder="1234 Hauler Ln, City, ST"
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Home Terminal Address</label>
                                    <input 
                                        type="text"
                                        value={localProfile.terminalAddress || ''}
                                        onChange={(e) => handleProfileChange('terminalAddress', e.target.value)}
                                        placeholder="5678 Base Ct, City, ST"
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>DOT #</label>
                                    <input 
                                        type="text"
                                        value={localProfile.dotNumber || ''}
                                        onChange={(e) => handleProfileChange('dotNumber', e.target.value)}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="profile-field">
                                    <label>Email Address</label>
                                    <input 
                                        type="email"
                                        value={localProfile.email || ''}
                                        onChange={(e) => handleProfileChange('email', e.target.value)}
                                    />
                                </div>
                            </div>
                        </section>

                        {/* App Preferences */}
                        <section className="settings-section glass-card">
                            <h3 className="section-title">
                                <SettingsIcon size={20} />
                                App Preferences
                            </h3>
                            <div className="preferences-list">
                                <div className="preference-row">
                                    <div>
                                        <p className="pref-label">Automatic Log Backup</p>
                                        <p className="pref-desc">Save your logs to the cloud every hour</p>
                                    </div>
                                    <label className="toggle-switch">
                                        <input 
                                            type="checkbox" 
                                            checked={localProfile.autoBackup !== false} // Default true
                                            onChange={(e) => handleProfileChange('autoBackup', e.target.checked)}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <hr />
                                <div className="preference-row">
                                    <div>
                                        <p className="pref-label">HOS Compliance Alerts</p>
                                        <p className="pref-desc">Receive notifications before potential violations</p>
                                    </div>
                                    <label className="toggle-switch">
                                        <input 
                                            type="checkbox" 
                                            checked={localProfile.hosAlerts !== false} // Default true
                                            onChange={(e) => handleProfileChange('hosAlerts', e.target.checked)}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <hr />
                                <div className="preference-row">
                                    <div>
                                        <p className="pref-label">Measurement Units</p>
                                        <p className="pref-desc">Choose between Miles (US) or Kilometers (Metric)</p>
                                    </div>
                                    <select 
                                        style={{ color: darkMode ? 'white' : 'black', background: darkMode ? '#334155' : 'white' }} 
                                        className="units-select"
                                        value={localProfile.units || 'Miles (US)'}
                                        onChange={(e) => handleProfileChange('units', e.target.value)}
                                    >
                                        <option value="Miles (US)">Miles (US)</option>
                                        <option value="Kilometers (KM)">Kilometers (KM)</option>
                                    </select>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Sticky Save Bar */}
                    {hasChanges && (
                        <div className="save-bar">
                            <div className="save-bar-content">
                                <p>You have unsaved changes</p>
                                <div className="save-bar-actions">
                                    <button className="discard-btn" onClick={handleDiscard} disabled={isLoading}>Discard</button>
                                    <Button onClick={handleSave} disabled={isLoading}>
                                        {isLoading ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default SettingsPage;